/**
 * Toast — minimalistyczny system powiadomień. Singleton store + portal.
 * Bez zewnętrznej biblioteki (react-hot-toast itd.) — kontrolujemy w pełni styling.
 */
import { create } from 'zustand';
import { useEffect } from 'react';
import { Icons } from '../Icons';

export type ToastTone = 'info' | 'success' | 'error' | 'warn';

interface Toast {
  id: string;
  message: string;
  tone: ToastTone;
}

interface ToastStore {
  toasts: Toast[];
  show: (message: string, tone?: ToastTone) => void;
  dismiss: (id: string) => void;
}

const useToastStore = create<ToastStore>((set) => ({
  toasts: [],
  show: (message, tone = 'info') => {
    const id = `t-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    set((s) => ({ toasts: [...s.toasts, { id, message, tone }] }));
  },
  dismiss: (id) => set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),
}));

export const showToast = (message: string, tone: ToastTone = 'info') => useToastStore.getState().show(message, tone);

const toneClass: Record<ToastTone, { bg: string; border: string; icon: typeof Icons.Info }> = {
  info: { bg: 'bg-sky-50 dark:bg-sky-900/30', border: 'border-sky-200 dark:border-sky-800', icon: Icons.Info },
  success: { bg: 'bg-emerald-50 dark:bg-emerald-900/30', border: 'border-emerald-200 dark:border-emerald-800', icon: Icons.Success },
  error: { bg: 'bg-rose-50 dark:bg-rose-900/30', border: 'border-rose-200 dark:border-rose-800', icon: Icons.Error },
  warn: { bg: 'bg-amber-50 dark:bg-amber-900/30', border: 'border-amber-200 dark:border-amber-800', icon: Icons.Alert },
};

export function ToastViewport() {
  const toasts = useToastStore((s) => s.toasts);
  const dismiss = useToastStore((s) => s.dismiss);

  useEffect(() => {
    if (toasts.length === 0) return;
    const timers = toasts.map((t) => window.setTimeout(() => dismiss(t.id), 3500));
    return () => timers.forEach((id) => window.clearTimeout(id));
  }, [toasts, dismiss]);

  return (
    <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 flex flex-col gap-2 pointer-events-none">
      {toasts.map((t) => {
        const cfg = toneClass[t.tone];
        const Icon = cfg.icon;
        return (
          <div
            key={t.id}
            className={`pointer-events-auto px-4 py-3 rounded-xl border ${cfg.bg} ${cfg.border} shadow-lg flex items-center gap-2.5 min-w-[18rem] max-w-md text-sm font-medium text-slate-800 dark:text-slate-100 animate-in slide-in-from-top-2 fade-in duration-200`}
            role="status"
          >
            <Icon size={18} className="shrink-0" />
            <span className="flex-1">{t.message}</span>
            <button
              onClick={() => dismiss(t.id)}
              className="text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
              aria-label="Zamknij"
            >
              <Icons.X size={16} />
            </button>
          </div>
        );
      })}
    </div>
  );
}
